import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMinhasInstituicoesDetalhadas, getMinhasEmendas } from '../services/userService';
import type { InstituicaoDetalhada, EmendaResumida } from '../types/user.types';
import planoService from '../services/planoTrabalhoService';
import type { ComissaoMembroPainelPlano } from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import emendaService, { resolveEmendaStatus } from '../services/emendaService';
import institutionService from '../services/institutionService';
import dirigenteService from '../services/dirigenteService';
import documentoInstitucionalService from '../services/documentoInstitucionalService';
import type { AlertaInstituicao } from '../services/documentoInstitucionalService';
import diligenciaDocumentoService from '../services/diligenciaDocumentoService';
import type { DiligenciaDocumentoDTO } from '../services/diligenciaDocumentoService';
import documentoLiberacaoService, { type DocumentoLiberacao } from '../services/documentoLiberacaoService';
import parametroSistemaService, { type ParametroSistema } from '../services/parametroSistemaService';
import relatorioPrestacaoContasService, { type PrestacaoBemSolicitacao } from '../services/relatorioPrestacaoContasService';
import prestacaoContaService, { type PrestacaoAjusteSolicitacao } from '../services/prestacaoContaService';
import { toUpperText } from '../utils/formatters';

type PendenciaNotaFiscal = {
  emenda: EmendaResumida;
  documento: DocumentoLiberacao;
};

const isInstituicaoNaFila = (it: any) =>
  ['PENDENTE', 'AGUARDANDO_ANALISE', 'EM_ANALISE'].includes(it.statusAprovacao || '');
import ConveniosInstitutionsQueue from '../components/ConveniosInstitutionsQueue';

// Selo da fila de instituições: PENDENTE só chega aqui com documento esperando análise (cadastro ainda incompleto).
const FILA_INSTITUICAO_SELO: Record<string, { label: string; classe: string }> = {
  AGUARDANDO_ANALISE: { label: 'Aguardando análise', classe: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' },
  EM_ANALISE: { label: 'Em análise', classe: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' },
  PENDENTE: { label: 'Cadastro incompleto · documento a analisar', classe: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' },
};
import api from '../services/api';
import procuracaoService, { Procuracao } from '../services/procuracaoService';
import PlanosTrabalhoBox from '../components/dashboard/PlanosTrabalhoBox';
import EmendasRecentesBox from '../components/dashboard/EmendasRecentesBox';
import { emendaEtapa, etapaBadgeClass } from '../utils/emendaEtapa';
import LegislativeWorkspace from '../components/LegislativeWorkspace';
import ProcuradoriaWorkspace from '../components/ProcuradoriaWorkspace';
import GestorJuridicoWorkspace from '../components/GestorJuridicoWorkspace';
import BoxEmendaVereador from '../components/BoxEmendaVereador';
import WorkspaceOverview, { type WorkspaceMetric } from '../components/dashboard/WorkspaceOverview';
import WorkspaceSection from '../components/dashboard/WorkspaceSection';
import WorkspaceSectionGroup from '../components/dashboard/WorkspaceSectionGroup';
import WorkspaceTabPills from '../components/dashboard/WorkspaceTabPills';
import WorkspaceBlockerBanner from '../components/dashboard/WorkspaceBlockerBanner';
import { StatusOSCBadge } from '../components/StatusOSCComponents';
import { StatusOSC } from '../types/statusOSC.types';
import { notifyError } from '../utils/notify';

// Feature toggle: enable the 'Mover para fim' buttons (Convênios / Dirigentes)
// ------------------------------------------------------------
// Purpose:
// - This flag controls whether the UI renders the "Mover para fim" action for
//   institutions and dirigentes. The action calls backend endpoints that have
//   been unstable in the current environment (returned 500/504 intermittently).
//
// How to re-enable safely:
// 1) Ensure backend endpoints are stable and support these operations:
//      POST /institutions/{id}/fila/move-to-end
//      POST /dirigentes/{id}/fila/move-to-end  (if implemented server-side)
// 2) Optionally add server-side rate limiting or request-timeouts to avoid overload.
// 3) Flip the flag below to `true` and restart the frontend dev server.
// 4) Run manual tests and watch backend logs for errors (500/504) or timeouts.
//
// Notes:
// - The UI already keeps the move logic guarded and uses `movingIds` to prevent
//   duplicate clicks, and falls back to `listForUser()` if the queue endpoint fails.
// - Keep this flag `false` in production until you confirm backend reliability.
// ------------------------------------------------------------
const ENABLE_MOVE_TO_END = false;

const ROLE_CONFIG: Record<string, {
  label: string;
  corBg: string; corBorder: string; corText: string;
  steps: { label: string }[];
  statusPendentes: string[];
}> = {
  ORCAMENTO: {
    label: 'Orçamento',
    corBg: 'bg-amber-50', corBorder: 'border-amber-200', corText: 'text-amber-900',
    steps: [
      { label: 'Analisar admissibilidade' },
      { label: 'Aprovar / devolver ao legislativo' },
      { label: 'Tratar devoluções (Indireta)' },
    ],
    statusPendentes: ['Recebido', 'Em análise de admissibilidade', 'Retornada ao Orçamento (demanda)', 'Retornada ao Orçamento (documental)'],
  },
  SECRETARIA: {
    label: 'Secretaria',
    corBg: 'bg-teal-50', corBorder: 'border-teal-200', corText: 'text-teal-900',
    steps: [
      { label: 'Analisar demanda' },
      { label: 'Aprovar / devolver por incompatibilidade' },
      { label: 'Acompanhar execução direta' },
    ],
    statusPendentes: ['Admissibilidade aprovada', 'Em análise de demanda'],
  },
  CONVENIOS: {
    label: 'Convênios',
    corBg: 'bg-violet-50', corBorder: 'border-violet-200', corText: 'text-violet-900',
    steps: [
      { label: 'Analisar documentação (Indireta)' },
      { label: 'Aprovar / devolver documental' },
      { label: 'Formalizar termo/empenho' },
    ],
    statusPendentes: ['Análise de demanda aprovada', 'Em análise documental', 'Análise documental aprovada'],
  },
  JURIDICO: {
    label: 'Procuradoria Geral Municipal',
    corBg: 'bg-blue-50', corBorder: 'border-blue-200', corText: 'text-blue-900',
    steps: [
      { label: 'Emitir parecer em minutas/termos' },
      { label: 'Registrar exigências e devolver' },
    ],
    statusPendentes: ['Análise documental aprovada', 'Iniciado', 'Em execução'],
  },
  OPERADOR: {
    label: 'Operador',
    corBg: 'bg-indigo-50', corBorder: 'border-indigo-200', corText: 'text-indigo-900',
    steps: [
      { label: 'Enviar detalhamento e anexos' },
      { label: 'Corrigir pendências devolvidas' },
      { label: 'Acompanhar execução' },
    ],
    statusPendentes: ['Iniciado', 'Devolvido', 'Em execução'],
  },
  GESTOR_JURIDICO: {
    label: 'Gestor Jurídico',
    corBg: 'bg-slate-50', corBorder: 'border-slate-200', corText: 'text-slate-900',
    steps: [],
    statusPendentes: [],
  },
  ANALISTA: {
    label: 'Analista',
    corBg: 'bg-sky-50', corBorder: 'border-sky-200', corText: 'text-sky-900',
    steps: [{ label: 'Analisar planos da comissão' }, { label: 'Emitir parecer' }],
    statusPendentes: [],
  },
  JURIDICO_AUXILIAR: {
    label: 'Auxiliar PGM',
    corBg: 'bg-blue-50', corBorder: 'border-blue-200', corText: 'text-blue-900',
    steps: [{ label: 'Elaborar parecer jurídico' }, { label: 'Encaminhar ao supervisor' }],
    statusPendentes: [],
  },
  CAMARA: {
    label: 'Câmara Municipal',
    corBg: 'bg-slate-50', corBorder: 'border-slate-200', corText: 'text-slate-900',
    steps: [],
    statusPendentes: [],
  },
  VEREADOR: {
    label: 'Parlamentar',
    corBg: 'bg-slate-50', corBorder: 'border-slate-200', corText: 'text-slate-900',
    steps: [],
    statusPendentes: [],
  },
  ADMIN: {
    label: 'Administrador',
    corBg: 'bg-rose-50', corBorder: 'border-rose-200', corText: 'text-rose-900',
    steps: [],
    statusPendentes: [],
  },
};

const DashboardHomePage: React.FC = () => {
  const { user, isOperadorSecretaria } = useAuth();
  const navigate = useNavigate();

  const role = String(user?.role ?? '').toUpperCase();
  const roleConfig = ROLE_CONFIG[role];
  const isAdmin      = role === 'ADMIN';
  // Gestor da Parceria: atribuição booleana sobre SECRETARIA, não role próprio
  // (ver User.gestorParceria no backend) — mesmo padrão de isOperadorSecretaria.
  const isGestor     = Boolean(user?.gestorParceria);
  // O operador de secretaria é um OPERADOR (flag operadorSecretaria): mesmo workspace de gestão.
  const isOperador   = role === 'OPERADOR';
  const isConvenios  = role === 'CONVENIOS';
  const isComissaoWorkspace = ['SECRETARIA', 'ANALISTA'].includes(role);
  // Qualquer usuário pode ser membro de comissão — o painel é mostrado se houver planos
  const isLegislative = role === 'CAMARA' || role === 'VEREADOR';
  // Perfis que só atuam no fluxo — não gerenciam OSCs/planos
  const isWorkflow   = ['ORCAMENTO', 'SECRETARIA', 'CONVENIOS', 'JURIDICO'].includes(role);
  // OPERADOR não tem acesso à fila de gestão (/dashboard/gestao/planos-trabalho);
  // demais perfis devem usar a fila oficial em vez da lista pessoal do OPERADOR.
  const planosListPath = isOperador ? '/dashboard/planos' : '/dashboard/gestao/planos-trabalho';

  const [loading, setLoading]               = useState(true);
  const [todasEmendas, setTodasEmendas]     = useState<any[]>([]);
  const [emendas, setEmendas]               = useState<EmendaResumida[]>([]);
  const [instituicoes, setInstituicoes]     = useState<InstituicaoDetalhada[]>([]);
  const [planos, setPlanos]                 = useState<PlanoTrabalho[]>([]);
  const [planosComissao, setPlanosComissao] = useState<ComissaoMembroPainelPlano[]>([]);
  const [hasInstituicoes, setHasInstituicoes] = useState(false);
  const [procuracoesPendentes, setProcuracoesPendentes] = useState<Procuracao[]>([]);
  const [conveniosQueue, setConveniosQueue] = useState<any[]>([]);
  const [dirigentesQueue, setDirigentesQueue] = useState<any[]>([]);
  const [conveniosQueueDiag, setConveniosQueueDiag] = useState<string | null>(null);
  const [docsVencidos, setDocsVencidos] = useState<AlertaInstituicao[]>([]);
  const [diligencias, setDiligencias] = useState<DiligenciaDocumentoDTO[]>([]);
  const [notasFiscaisPendentes, setNotasFiscaisPendentes] = useState<PendenciaNotaFiscal[]>([]);
  const [bensDevolucaoSolicitada, setBensDevolucaoSolicitada] = useState<PrestacaoBemSolicitacao[]>([]);
  const [ajustesSolicitados, setAjustesSolicitados] = useState<PrestacaoAjusteSolicitacao[]>([]);
  const [parametroSistema, setParametroSistema] = useState<ParametroSistema | null>(null);
  // Operador de secretaria tem data final extra na janela de pré-projeto; sem ela vale a data final geral.
  const fimJanelaPreProjeto = isOperadorSecretaria && parametroSistema?.preProjetoJanelaFim
    && parametroSistema.preProjetoJanelaFimSecretaria
    ? parametroSistema.preProjetoJanelaFimSecretaria
    : parametroSistema?.preProjetoJanelaFim;
  const [emendaFilter, setEmendaFilter] = useState<'todas' | 'devolvidas' | 'iniciadas' | 'concluidas'>('todas');
  const [resolvendoDiligenciaId, setResolvendoDiligenciaId] = useState<string | null>(null);
  const [resolverObservacao, setResolverObservacao] = useState('');
  const [resolvendoLoading, setResolvendoLoading] = useState(false);
  // Área de trabalho do Convênios
  const [diligenciasPendentesOperador, setDiligenciasPendentesOperador] = useState<DiligenciaDocumentoDTO[]>([]);
  const [diligenciasParaConfirmar, setDiligenciasParaConfirmar] = useState<DiligenciaDocumentoDTO[]>([]);
  const [verificandoId, setVerificandoId] = useState<string | null>(null);
  const [diligenciasTab, setDiligenciasTab] = useState<'confirmar' | 'operador' | null>(null);
  const [cadastrosTab, setCadastrosTab] = useState<'instituicoes' | 'diretores' | 'documentos' | null>(null);
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(new Set());
  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);
  const renderCollapseButton = (id: string, className = 'border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800') => (
    <button
      type="button"
      onClick={() => toggleBox(id)}
      className={`shrink-0 rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${className}`}
    >
      {isBoxCollapsed(id) ? 'Expandir' : 'Recolher'}
    </button>
  );
  const [movingIds, setMovingIds] = useState<string[]>([]);
  const [workspaceTab, setWorkspaceTab] = useState<'acoes' | 'dashboard'>('acoes');
  const pollRef = React.useRef<number | null>(null);
  const isFetchingRef = React.useRef(false);
  const fixedCardClass = 'flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border shadow-sm';
  const fixedCardBodyClass = 'min-h-0 flex-1 overflow-y-auto';

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const load = async () => {
      try {
        // isGestor (Gestor da Parceria): mesma varredura ampla de instituicoes/planos que
        // ADMIN tinha quando esse era um role proprio — agora e uma flag sobre SECRETARIA.
        if (isAdmin || isGestor) {
          const [insts, allPlanos] = await Promise.all([
            getMinhasInstituicoesDetalhadas().catch(() => [] as InstituicaoDetalhada[]),
            planoService.listAll().catch(() => [] as PlanoTrabalho[]),
          ]);
          setInstituicoes(insts);
          setHasInstituicoes(insts.length > 0);
          setPlanos(allPlanos);
          if (insts.length > 0) {
            getMinhasEmendas()
              .then(setEmendas)
              .catch(() => setEmendas([] as EmendaResumida[]));
          }
        } else if (isOperador) {
          const [insts, allPlanos] = await Promise.all([
            getMinhasInstituicoesDetalhadas().catch(() => [] as InstituicaoDetalhada[]),
            planoService.listAll().catch(() => [] as PlanoTrabalho[]),
          ]);
          setInstituicoes(insts);
          setHasInstituicoes(insts.length > 0);
          procuracaoService.recebidas()
            .then(list => setProcuracoesPendentes(list.filter(p => p.status === 'PENDENTE')))
            .catch(() => setProcuracoesPendentes([]));
          relatorioPrestacaoContasService.getSolicitacoesDevolucaoPendentes()
            .then(setBensDevolucaoSolicitada)
            .catch(() => setBensDevolucaoSolicitada([]));
          prestacaoContaService.getAjustesPendentes()
            .then(setAjustesSolicitados)
            .catch(() => setAjustesSolicitados([]));
          if (insts.length > 0) {
            const ids = new Set(insts.map(i => i.id));
            setPlanos(allPlanos.filter(p => !p.instituicaoId || ids.has(p.instituicaoId)));
            const minhasEmendas = await getMinhasEmendas().catch(() => [] as EmendaResumida[]);
            setEmendas(minhasEmendas);
            const documentos = await Promise.allSettled(
              minhasEmendas.map(async (emenda) => ({
                emenda,
                documento: await documentoLiberacaoService.get(emenda.id),
              })),
            );
            setNotasFiscaisPendentes(documentos.flatMap((resultado) => {
              if (resultado.status !== 'fulfilled') return [];
              return ['AGUARDANDO_ENVIO', 'DEVOLVIDO_CORRECAO'].includes(resultado.value.documento.status)
                ? [resultado.value]
                : [];
            }));
            Promise.all(
              insts.map(inst => diligenciaDocumentoService.listar(inst.id).then(r => r.data).catch(() => [] as DiligenciaDocumentoDTO[]))
            )
              .then(todasDiligencias => setDiligencias(todasDiligencias.flat()))
              .catch(() => setDiligencias([]));
          }
        }
        // isWorkflow: não precisa de instituições nem planos (exceto CONVENIOS que carrega planos)
        if (role === 'CONVENIOS') {
          const [planosConvenios, alertasVenc, pendentesOp, paraConfirmar] = await Promise.all([
            planoService.listAll().catch(() => [] as PlanoTrabalho[]),
            documentoInstitucionalService.alertasVencimento().catch(() => [] as AlertaInstituicao[]),
            diligenciaDocumentoService.listarPendentes().then(r => r.data).catch(() => [] as DiligenciaDocumentoDTO[]),
            diligenciaDocumentoService.listarEmVerificacao().then(r => r.data).catch(() => [] as DiligenciaDocumentoDTO[]),
          ]);
          setPlanos(planosConvenios);
          setDocsVencidos(alertasVenc.filter(a => a.totalVencidos > 0));
          setDiligenciasPendentesOperador(pendentesOp);
          setDiligenciasParaConfirmar(paraConfirmar);
        }
        // If user is CONVENIOS, fetch the institutions queue
        if (role === 'CONVENIOS') {
          try {
            console.debug('[DashboardHomePage] fetching convenios queue...');
            let q: any[] = [];
            try {
              // Request queue and surface errors so we can show diagnostics in the UI
              q = await institutionService.getQueue(undefined, { retries: 2, swallowErrors: false });
              setConveniosQueueDiag(null);
            } catch (e: any) {
              console.warn('[DashboardHomePage] institutionService.getQueue failed (diagnostic):', e);
              // store diagnostic message for UI/triage
              setConveniosQueueDiag(e?.message || String(e));
              q = [];
            }
            console.debug('[DashboardHomePage] convenios queue fetched length=', Array.isArray(q) ? q.length : 'not-array', q);

            // Fallback: if queue is empty, try listing institutions visible to user and filter by approval status
            if (!Array.isArray(q) || q.length === 0) {
              console.debug('[DashboardHomePage] queue empty — attempting fallback to institutions listForUser');
              try {
                const allForUser = await institutionService.listForUser().catch((e) => { console.warn('[DashboardHomePage] listForUser failed', e); return []; });
                const filtered = (allForUser || []).filter((it: any) => isInstituicaoNaFila(it));
                console.debug('[DashboardHomePage] fallback institutions for user count=', filtered.length);
                q = filtered;
              } catch (fbErr) {
                console.warn('[DashboardHomePage] fallback failed', fbErr);
              }
            }

            setConveniosQueue(q);

            console.debug('[DashboardHomePage] fetching dirigentes queue...');
            const dq = await dirigenteService.getQueue().catch((e) => {
              console.warn('[DashboardHomePage] dirigenteService.getQueue failed:', e);
              return [];
            });
            console.debug('[DashboardHomePage] dirigentes queue fetched length=', Array.isArray(dq) ? dq.length : 'not-array', dq);
            setDirigentesQueue(dq);
          } catch (err) {
            console.error('[DashboardHomePage] error loading queues:', err);
            setConveniosQueue([]);
            setDirigentesQueue([]);
          }
        }
        // isWorkflow (ORCAMENTO/SECRETARIA/CONVENIOS/JURIDICO) precisa de todasEmendas mesmo
        // quando o usuario tambem e Gestor da Parceria — a flag e aditiva ao papel SECRETARIA,
        // nao substitui as responsabilidades normais de analise de demanda.
        if (!isAdmin && !isOperador && role !== 'CONVENIOS') {
          emendaService.listWithDetails()
            .then(setTodasEmendas)
            .catch(() => setTodasEmendas([]));
        }
        planoService.listarPlanosDaMinhaComissao()
          .then(setPlanosComissao)
          .catch(() => setPlanosComissao([] as ComissaoMembroPainelPlano[]));
      } catch {
        setHasInstituicoes(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Aviso de período de pré-projeto — só interessa a quem gerencia instituição.
  useEffect(() => {
    if (!user || !isOperador) return;
    parametroSistemaService.getParametros()
      .then(setParametroSistema)
      .catch(() => setParametroSistema(null));
  }, [user, isOperador]);

  // Polling effect: periodically refresh queues for CONVENIOS users
  React.useEffect(() => {
    const POLL_INTERVAL_MS = 15000; // 15 seconds

    async function fetchQueues() {
      // Prevent overlapping fetches and don't fetch when tab is hidden
      if (isFetchingRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;

      isFetchingRef.current = true;
      try {
        // Fetch with diagnostics: prefer to get detailed error info if queue endpoint fails
        let q: any[] = [];
        let dq: any[] = [];
        try {
          q = await institutionService.getQueue(undefined, { retries: 1, swallowErrors: false });
          setConveniosQueueDiag(null);
        } catch (e: any) {
          console.warn('[DashboardHomePage polling] institutionService.getQueue failed:', e);
          setConveniosQueueDiag(e?.message || String(e));
          q = [];
        }
        dq = await dirigenteService.getQueue().catch(() => []);

         // If queue is empty, attempt fallback to institutions/for-user filtering
         if (!Array.isArray(q) || q.length === 0) {
           console.debug('[DashboardHomePage polling] queue empty — attempting fallback to listForUser');
           try {
             const allForUser = await institutionService.listForUser().catch((e) => { console.warn('[DashboardHomePage polling] listForUser failed', e); return []; });
             q = (allForUser || []).filter((it: any) => isInstituicaoNaFila(it));
             console.debug('[DashboardHomePage polling] fallback institutions count=', (q || []).length);
           } catch (e) {
             console.warn('[DashboardHomePage polling] fallback failed', e);
           }
         }

         setConveniosQueue(q);
         setDirigentesQueue(dq);
      } catch (e) {
        console.warn('Erro ao atualizar filas via polling:', e);
      } finally {
        isFetchingRef.current = false;
      }
    }

    // Start polling only for CONVENIOS role
    if (role === 'CONVENIOS') {
      // Ensure we start with a fresh fetch (if not already loaded)
      fetchQueues();
      // Use window.setInterval so the returned id is a number
      pollRef.current = window.setInterval(fetchQueues, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      // reset fetching flag on cleanup
      isFetchingRef.current = false;
    };
  }, [role]);

  // Emendas filtradas pelo perfil atual
  const pendentes = roleConfig?.statusPendentes.length
    ? todasEmendas.filter(e => roleConfig.statusPendentes.includes(resolveEmendaStatus(e)))
    : [];

  // Emendas retornadas ao Orçamento aguardam a formalização da devolução à Câmara.
  // São separadas em um box próprio, antes das demais pendências de ação.
  const STATUS_AGUARDANDO_DEVOLUCAO = ['Retornada ao Orçamento (demanda)', 'Retornada ao Orçamento (documental)'];
  const aguardandoDevolucao = pendentes.filter(e => STATUS_AGUARDANDO_DEVOLUCAO.includes(resolveEmendaStatus(e)));
  const aguardandoAcao = pendentes.filter(e => !STATUS_AGUARDANDO_DEVOLUCAO.includes(resolveEmendaStatus(e)));

  // Stats
  const statsEmendas  = isWorkflow ? todasEmendas : emendas;
  const totalValor    = isWorkflow ? 0 : (emendas as EmendaResumida[]).reduce((a, e) => a + (e.valor ?? 0), 0);
  const concluidas    = statsEmendas.filter(e => e.status === 'Concluído').length;
  const devolvidas    = statsEmendas.filter(e => (e.status ?? '').toLowerCase().startsWith('devolvid')).length;
  const emCurso       = statsEmendas.length - concluidas - devolvidas;

  // Emendas Recentes — categorização por status para os chips de filtro.
  const emendaCategoria = (e: EmendaResumida): 'devolvidas' | 'concluidas' | 'iniciadas' => {
    const s = (e.status ?? '').toLowerCase();
    if (s.startsWith('devolvid')) return 'devolvidas';
    if (s.startsWith('conclu')) return 'concluidas';
    return 'iniciadas';
  };
  const emendasList = emendas as EmendaResumida[];
  const emendasCountDevolvidas = emendasList.filter(e => emendaCategoria(e) === 'devolvidas').length;
  const emendasCountConcluidas = emendasList.filter(e => emendaCategoria(e) === 'concluidas').length;
  const emendasCountIniciadas  = emendasList.filter(e => emendaCategoria(e) === 'iniciadas').length;
  const emendasFiltradas = emendaFilter === 'todas'
    ? emendasList
    : emendasList.filter(e => emendaCategoria(e) === emendaFilter);

  const planosEmAndamento = planos.filter(plano => !['CONCLUIDO', 'FECHADO'].includes(plano.status || '')).length;
  const pareceresPendentes = planosComissao.filter(plano => plano.parecerPendente).length;
  const pendenciasOperador = diligencias.filter(diligencia => diligencia.status === 'PENDENTE').length + notasFiscaisPendentes.length;

  const operatorMetrics: WorkspaceMetric[] = [
    { label: 'Instituições', value: instituicoes.length, tone: 'neutral' },
    { label: 'Emendas', value: emendas.length, tone: 'progress' },
    { label: 'Planos em andamento', value: planosEmAndamento, tone: 'warning' },
    { label: 'Pendências documentais', value: pendenciasOperador, tone: 'critical' },
  ];

  const workspacePresentation: Record<string, { description: string; metrics: WorkspaceMetric[] }> = {
    ADMIN: {
      description: 'Visão operacional dos registros e fluxos acompanhados.',
      metrics: [
        { label: 'Instituições vinculadas', value: instituicoes.length, tone: 'neutral' },
        { label: 'Emendas vinculadas', value: emendas.length, tone: 'progress' },
        { label: 'Planos no sistema', value: planos.length, tone: 'warning' },
        { label: 'Pareceres da comissão', value: pareceresPendentes, tone: 'critical' },
      ],
    },
    ORCAMENTO: {
      description: 'Admissibilidade e devoluções ordenadas por prioridade.',
      metrics: [
        { label: 'Pendentes para mim', value: pendentes.length, tone: 'warning' },
        { label: 'Em andamento', value: emCurso, tone: 'progress' },
        { label: 'Devolvidas', value: devolvidas, tone: 'critical' },
        { label: 'Total visível', value: todasEmendas.length, tone: 'neutral' },
      ],
    },
    SECRETARIA: {
      description: 'Demandas da secretaria e análises sob sua responsabilidade.',
      metrics: [
        { label: 'Pendentes para mim', value: pendentes.length, tone: 'warning' },
        { label: 'Em andamento', value: emCurso, tone: 'progress' },
        { label: 'Planos da comissão', value: planosComissao.length, tone: 'neutral' },
        { label: 'Pareceres pendentes', value: pareceresPendentes, tone: 'critical' },
      ],
    },
    CONVENIOS: {
      description: 'Formalização, documentos e diligências ordenados por prioridade.',
      metrics: [
        { label: 'Planos em andamento', value: planosEmAndamento, tone: 'progress' },
        { label: 'Instituições na fila', value: conveniosQueue.length, tone: 'warning' },
        { label: 'Instituições com vencidos', value: docsVencidos.length, tone: 'critical' },
        { label: 'Diligências para confirmar', value: diligenciasParaConfirmar.length, tone: 'success' },
      ],
    },
    OPERADOR: {
      description: 'Entregas da instituição e pendências que exigem correção.',
      metrics: operatorMetrics,
    },
    OPERADOR_SECRETARIA: {
      description: 'Entregas da secretaria e pendências que exigem correção.',
      metrics: operatorMetrics,
    },
    ANALISTA: {
      description: 'Planos da comissão e pareceres aguardando análise.',
      metrics: [
        { label: 'Emendas visíveis', value: todasEmendas.length, tone: 'neutral' },
        { label: 'Planos da comissão', value: planosComissao.length, tone: 'progress' },
        { label: 'Pareceres pendentes', value: pareceresPendentes, tone: 'critical' },
        { label: 'Em andamento', value: emCurso, tone: 'warning' },
      ],
    },
  };
  const presentationKey = isOperadorSecretaria ? 'OPERADOR_SECRETARIA' : role;
  const workspaceOverview = workspacePresentation[presentationKey] ?? {
    description: 'Atividades e registros disponíveis para o perfil atual.',
    metrics: [],
  };

  const canViewVereadorDashboard = isAdmin || role === 'ORCAMENTO';
  const showActionsTab = !canViewVereadorDashboard || workspaceTab === 'acoes';

  if (isLegislative) return <LegislativeWorkspace />;
  if (role === 'JURIDICO' || role === 'JURIDICO_AUXILIAR') {
    return <ProcuradoriaWorkspace auxiliary={role === 'JURIDICO_AUXILIAR'} />;
  }
  if (role === 'GESTOR_JURIDICO') return <GestorJuridicoWorkspace />;

  return (
    <div className="w-full space-y-6">

      <WorkspaceOverview
        profile={roleConfig?.label ?? 'Perfil'}
        description={workspaceOverview.description}
        metrics={workspaceOverview.metrics}
        loading={loading}
      />

      {/* Box emendas por vereador — visível para ADMIN e ORCAMENTO */}
      {canViewVereadorDashboard && (
        <div className="flex flex-wrap gap-2 rounded-3xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <button
            type="button"
            onClick={() => setWorkspaceTab('acoes')}
            className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
              workspaceTab === 'acoes'
                ? 'bg-blue-700 text-white shadow-sm hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500'
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            Ações
          </button>
          <button
            type="button"
            onClick={() => setWorkspaceTab('dashboard')}
            className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
              workspaceTab === 'dashboard'
                ? 'bg-blue-700 text-white shadow-sm hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500'
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            Dashboard
          </button>
        </div>
      )}

      {canViewVereadorDashboard && workspaceTab === 'dashboard' && (
        <BoxEmendaVereador isVereador={false} />
      )}

      {showActionsTab && (
        <>

      {/* Aviso de instituição bloqueada (ex.: prestação de contas pendente) */}
      {!loading && isOperador && (
        <WorkspaceBlockerBanner
          title="Instituição bloqueada"
          items={instituicoes.filter((inst) => inst.bloqueada).map((inst) => ({
            title: inst.razaoSocial || inst.nomeFantasia,
            description: inst.motivoBloqueio,
          }))}
        />
      )}

      {/* Aviso de instituição reprovada: motivo e caminho para ajustar e reenviar */}
      {!loading && isOperador && (
        <WorkspaceBlockerBanner
          title="Instituição reprovada"
          items={instituicoes.filter((inst) => inst.statusOSC === 'REPROVADO').map((inst) => ({
            title: inst.razaoSocial || inst.nomeFantasia || '',
            description: inst.motivoReprovacao
              ? `Justificativa: ${inst.motivoReprovacao}`
              : 'Ajuste o cadastro e reenvie para análise.',
            actionLabel: 'Ajustar e reenviar',
            onAction: () => navigate(`/dashboard/cadastro-dados-institucionais?id=${encodeURIComponent(inst.id)}`),
          }))}
        />
      )}

      {/* Aviso de retificação de pré-projetos aberta — só para instituições não bloqueadas */}
      {!loading && isOperador && parametroSistema?.preProjetoRetificacaoAberta && instituicoes.some((inst) => !inst.bloqueada) && (
        <div className={`${fixedCardClass} border-amber-200 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950/20`}>
          <div className="border-b border-amber-200 bg-amber-100/60 px-5 py-3 dark:border-amber-500/40 dark:bg-amber-950/30">
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Período de retificação de pré-projetos aberto</h2>
          </div>
          <div className={`${fixedCardBodyClass} px-5 py-3`}>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Você pode alterar o valor e a área de atuação dos pré-projetos publicados e ainda não selecionados,
              informando o motivo, e também cadastrar e publicar novos pré-projetos
              {parametroSistema.preProjetoRetificacaoFim
                ? ` até ${new Date(`${parametroSistema.preProjetoRetificacaoFim}T00:00:00`).toLocaleDateString('pt-BR')}.`
                : '.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard/pre-projetos')}
              className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-amber-950/40"
            >
              Ver pré-projetos
            </button>
          </div>
        </div>
      )}

      {/* Aviso de período de pré-projeto aberto — só para instituições não bloqueadas */}
      {!loading && isOperador && parametroSistema?.preProjetoAberto && instituicoes.some((inst) => !inst.bloqueada) && (
        <div className={`${fixedCardClass} border-emerald-200 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-950/20`}>
          <div className="border-b border-emerald-200 bg-emerald-100/60 px-5 py-3 dark:border-emerald-500/40 dark:bg-emerald-950/30">
            <h2 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Período de pré-projeto aberto</h2>
          </div>
          <div className={`${fixedCardBodyClass} px-5 py-3`}>
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              O lançamento de pré-projeto para o próximo exercício está disponível
              {fimJanelaPreProjeto
                ? ` até ${new Date(`${fimJanelaPreProjeto}T00:00:00`).toLocaleDateString('pt-BR')}.`
                : ', sem data de encerramento definida ainda.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard/pre-projetos/novo')}
              className="mt-3 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-slate-900 dark:text-emerald-200 dark:hover:bg-emerald-950/40"
            >
              Cadastrar pré-projeto
            </button>
          </div>
        </div>
      )}

      {/* Pendências por perfil — um WorkspaceSectionGroup por família de papel; isGestor
          é aditivo (Secretaria + flag mostra os dois grupos), as demais são mutuamente
          exclusivas por `role`. */}
      {!loading && (() => {
        const pendentesGestor = planos.filter(p => p.etapaAtual === 'GESTOR_PARCERIA' && p.status === 'EM_ANALISE');
        const pendentesRetificacao = planos.filter(p => p.status === 'PENDENTE_RETIFICACAO');
        const abertasDiligencias = diligencias.filter(d => d.status !== 'RESOLVIDA');

        const aguardandoDevolucaoSection = aguardandoDevolucao.length > 0 ? (
          <WorkspaceSection color="rose" title="Emendas aguardando devolução" count={aguardandoDevolucao.length}>
            <div className="divide-y divide-rose-100 border-t border-rose-100 dark:divide-rose-900/40 dark:border-rose-900/40">
              {aguardandoDevolucao.slice(0, 8).map((e: any) => (
                <button key={e.id} type="button" onClick={() => navigate(`/dashboard/emendas/${e.id}`)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-rose-50 dark:hover:bg-rose-950/40">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{e.officialCode || 'Sem codigo'}</p>
                    <p className="truncate text-xs text-slate-600 dark:text-slate-400">{e.councilorName || e.councilorId || '—'}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-800 dark:bg-slate-900 dark:text-rose-200">
                    {resolveEmendaStatus(e)}
                  </span>
                </button>
              ))}
              {aguardandoDevolucao.length > 8 && (
                <button type="button" onClick={() => navigate('/dashboard/emendas')}
                  className="w-full px-5 py-3 text-left text-xs font-semibold text-rose-800 hover:underline dark:text-rose-200">
                  + {aguardandoDevolucao.length - 8} emendas a mais. Ver todas
                </button>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const aguardandoAcaoSection = aguardandoAcao.length > 0 ? (
          <WorkspaceSection color="amber" title="Emendas aguardando sua ação" count={aguardandoAcao.length}>
            <div className="divide-y divide-amber-100 border-t border-amber-100 dark:divide-amber-900/40 dark:border-amber-900/40">
              {aguardandoAcao.slice(0, 8).map((e: any) => (
                <button key={e.id} type="button" onClick={() => navigate(`/dashboard/emendas/${e.id}`)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-amber-50 dark:hover:bg-amber-950/40">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{e.officialCode || 'Sem codigo'}</p>
                    <p className="truncate text-xs text-slate-600 dark:text-slate-400">{e.councilorName || e.councilorId || '—'}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-slate-900 dark:text-amber-200">
                    {resolveEmendaStatus(e)}
                  </span>
                </button>
              ))}
              {aguardandoAcao.length > 8 && (
                <button type="button" onClick={() => navigate('/dashboard/emendas')}
                  className="w-full px-5 py-3 text-left text-xs font-semibold text-amber-800 hover:underline dark:text-amber-200">
                  + {aguardandoAcao.length - 8} emendas a mais. Ver todas
                </button>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const gestorDecisaoSection = pendentesGestor.length > 0 ? (
          <WorkspaceSection
            color="amber"
            title={pendentesGestor.length === 1 ? '1 plano aguardando sua decisão' : `${pendentesGestor.length} planos aguardando sua decisão`}
            subtitle="Os planos abaixo estão na etapa Gestor da Parceria e aguardam seu parecer técnico e decisão."
            collapsed={isBoxCollapsed('planos-gestor')}
            onToggleCollapse={() => toggleBox('planos-gestor')}
          >
            <div className="space-y-1.5 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
              {pendentesGestor.slice(0, 5).map(p => (
                <div key={p.id} onClick={() => navigate(`/dashboard/plano/full/${p.id}`)}
                  className="flex cursor-pointer items-center justify-between rounded-xl border border-amber-200 bg-white/80 px-3 py-2 transition-colors hover:bg-white dark:border-amber-500/40 dark:bg-slate-950/70 dark:hover:bg-slate-900">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-800 truncate block">{p.titulo}</span>
                    {p.emendaCodigo && <span className="text-xs text-slate-400">Emenda: {p.emendaCodigo}</span>}
                  </div>
                  <span className="ml-3 shrink-0 text-xs text-amber-700 font-medium">Analisar</span>
                </div>
              ))}
              {pendentesGestor.length > 5 && (
                <button type="button" onClick={() => navigate('/dashboard/planos-gestao')}
                  className="text-xs font-medium text-amber-700 underline pl-1 hover:text-amber-900">
                  {pendentesGestor.length - 5} planos a mais. Ver fila completa
                </button>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const notasFiscaisSection = notasFiscaisPendentes.length > 0 ? (
          <WorkspaceSection
            color="amber"
            title="Notas fiscais pendentes de lançamento"
            count={notasFiscaisPendentes.length}
            collapsed={isBoxCollapsed('notas-fiscais-pendentes')}
            onToggleCollapse={() => toggleBox('notas-fiscais-pendentes')}
          >
            <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
              {notasFiscaisPendentes.slice(0, 5).map(({ emenda, documento }) => (
                <button key={emenda.id} type="button" onClick={() => navigate(`/dashboard/execucao-financeira/${emenda.id}#documento-liberacao`)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-amber-50 dark:hover:bg-amber-950/20">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">Emenda {emenda.codigoOficial || emenda.id}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{emenda.instituicaoNome}</p>
                    {documento.prazoEnvio && <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">Prazo: {new Date(`${documento.prazoEnvio}T12:00:00`).toLocaleDateString('pt-BR')}</p>}
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    {documento.status === 'DEVOLVIDO_CORRECAO' ? 'Corrigir nota' : 'Lançar nota'}
                  </span>
                </button>
              ))}
              {notasFiscaisPendentes.length > 5 && (
                <div className="px-4 py-2.5 text-center text-xs font-medium text-amber-700 dark:text-amber-300">
                  Mais {notasFiscaisPendentes.length - 5} pendência(s)
                </div>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const ajustesSolicitadosSection = ajustesSolicitados.length > 0 ? (
          <WorkspaceSection
            color="rose"
            title="Ajustes solicitados na prestação de contas"
            count={ajustesSolicitados.length}
            collapsed={isBoxCollapsed('ajustes-solicitados')}
            onToggleCollapse={() => toggleBox('ajustes-solicitados')}
          >
            <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
              {ajustesSolicitados.slice(0, 5).map((a) => (
                <button key={a.pcId} type="button" onClick={() => navigate(`/dashboard/plano/prestacao-contas?planoId=${a.planoTrabalhoId}`)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/20">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                      {a.instituicaoNome} — {a.planoTitulo} {a.tipo === 'UNICA' ? '(PC única)' : a.numero ? `(PC ${a.numero})` : ''}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{a.mensagem}</p>
                    <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-400">
                      Solicitado por {a.solicitadoPor || 'Convênios'} em {new Date(a.solicitadoEm).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-rose-700 dark:text-rose-300">Ver PC</span>
                </button>
              ))}
              {ajustesSolicitados.length > 5 && (
                <div className="px-4 py-2.5 text-center text-xs font-medium text-rose-700 dark:text-rose-300">
                  Mais {ajustesSolicitados.length - 5} pendência(s)
                </div>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const bensDevolucaoSection = bensDevolucaoSolicitada.length > 0 ? (
          <WorkspaceSection
            color="amber"
            title="Devolução de bens solicitada"
            count={bensDevolucaoSolicitada.length}
            collapsed={isBoxCollapsed('bens-devolucao-solicitada')}
            onToggleCollapse={() => toggleBox('bens-devolucao-solicitada')}
          >
            <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
              {bensDevolucaoSolicitada.slice(0, 5).map((b) => (
                <button key={b.bemId} type="button" onClick={() => navigate(`/dashboard/plano/prestacao-contas?planoId=${b.planoTrabalhoId}`)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-amber-50 dark:hover:bg-amber-950/20">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{b.especificacao || b.documentoNumero || 'Bem adquirido'}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{b.instituicaoNome} — {b.planoTitulo}</p>
                    <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                      Solicitado por {b.devolucaoSolicitadaPor || 'Convênios'} em {new Date(b.devolucaoSolicitadaEm).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-300">Ver bem</span>
                </button>
              ))}
              {bensDevolucaoSolicitada.length > 5 && (
                <div className="px-4 py-2.5 text-center text-xs font-medium text-amber-700 dark:text-amber-300">
                  Mais {bensDevolucaoSolicitada.length - 5} pendência(s)
                </div>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const diligenciasResolverSection = abertasDiligencias.length > 0 ? (
          <WorkspaceSection
            color="amber"
            title="Diligências para resolver"
            count={abertasDiligencias.length}
            actionLabel="Ver detalhes"
            onAction={() => navigate(abertasDiligencias[0]?.instituicaoId
              ? `/dashboard/cadastro-dados-institucionais?id=${abertasDiligencias[0].instituicaoId}`
              : '/dashboard/cadastro-dados-institucionais')}
            collapsed={isBoxCollapsed('diligencias-resolver')}
            onToggleCollapse={() => toggleBox('diligencias-resolver')}
          >
            <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
              {abertasDiligencias.slice(0, 5).map(d => {
                const emVerificacao = d.status === 'EM_VERIFICACAO';
                const rejeitada = Boolean(d.rejeicaoMotivo) && d.status === 'PENDENTE' && d.verificadoEm;
                return (
                  <div key={d.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 line-clamp-2">{d.mensagem}</p>
                        {rejeitada && (
                          <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
                            Convênios rejeitou: {d.rejeicaoMotivo}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-slate-400">{d.enviadoPor} · {d.enviadoEm ? new Date(d.enviadoEm).toLocaleDateString('pt-BR') : ''}</span>
                          {d.planoTrabalhoId && (
                            <button type="button" onClick={() => navigate(`/dashboard/plano/full/${d.planoTrabalhoId}`)}
                              className="text-xs font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-400">
                              Ver plano de trabalho
                            </button>
                          )}
                        </div>
                      </div>
                      {emVerificacao ? (
                        <span className="shrink-0 inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          Aguardando confirmação
                        </span>
                      ) : (
                        <button type="button" onClick={() => { setResolvendoDiligenciaId(d.id); setResolverObservacao(''); }}
                          className="shrink-0 rounded-lg border border-emerald-400 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          Marcar como resolvida
                        </button>
                      )}
                    </div>
                    {resolvendoDiligenciaId === d.id && (
                      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 dark:border-emerald-800 dark:bg-emerald-950/30">
                        <textarea
                          rows={2}
                          value={resolverObservacao}
                          onChange={e => setResolverObservacao(toUpperText(e.target.value))}
                          placeholder="Descreva o que foi providenciado (opcional)..."
                          className="w-full rounded-lg border border-emerald-300 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-emerald-500 focus:outline-none dark:border-emerald-700 dark:bg-slate-900 dark:text-slate-100"
                        />
                        <div className="mt-2 flex justify-end gap-2">
                          <button type="button" onClick={() => setResolvendoDiligenciaId(null)}
                            className="rounded-lg border border-slate-300 px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300">
                            Cancelar
                          </button>
                          <button
                            type="button"
                            disabled={resolvendoLoading}
                            onClick={async () => {
                              setResolvendoLoading(true);
                              try {
                                const res = await diligenciaDocumentoService.resolver(d.id, resolverObservacao);
                                setDiligencias(prev => prev.map(item => item.id === d.id ? res.data : item));
                                setResolvendoDiligenciaId(null);
                              } catch {
                                notifyError('Erro ao enviar para verificação. Tente novamente.');
                              } finally {
                                setResolvendoLoading(false);
                              }
                            }}
                            className="rounded-lg bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {resolvendoLoading ? 'Enviando...' : 'Enviar para verificação'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </WorkspaceSection>
        ) : null;

        const renderRetificacaoSection = (operadorContext: boolean) => pendentesRetificacao.length > 0 ? (
          <WorkspaceSection
            color="orange"
            title={pendentesRetificacao.length === 1 ? '1 plano pendente de retificação' : `${pendentesRetificacao.length} planos pendentes de retificação`}
            subtitle={operadorContext
              ? 'A Procuradoria Geral Municipal solicitou ajustes. Corrija e envie a retificação para retornar à análise.'
              : 'A Procuradoria Geral Municipal solicitou ajustes. Aguardando o operador corrigir e reenviar para análise.'}
            collapsed={isBoxCollapsed('retificacoes')}
            onToggleCollapse={() => toggleBox('retificacoes')}
          >
            <div className="space-y-1.5 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
              {pendentesRetificacao.slice(0, 5).map(p => (
                <div key={p.id} onClick={() => navigate(`/dashboard/plano/full/${p.id}`)}
                  className="flex items-center justify-between rounded-xl border border-orange-200 bg-white/80 px-3 py-2 cursor-pointer hover:bg-white transition-colors dark:border-orange-500/40 dark:bg-slate-950/70 dark:hover:bg-slate-900">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-800 truncate block dark:text-slate-200">{p.titulo}</span>
                    {p.emendaCodigo && <span className="text-xs text-slate-400">Emenda: {p.emendaCodigo}</span>}
                  </div>
                  <span className="ml-3 shrink-0 text-xs text-orange-700 font-medium dark:text-orange-400">{operadorContext ? 'Retificar' : 'Ver'}</span>
                </div>
              ))}
              {pendentesRetificacao.length > 5 && (
                <button type="button" onClick={() => navigate('/dashboard/gestao/planos-trabalho')}
                  className="text-xs font-medium text-orange-700 underline pl-1 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-200">
                  {pendentesRetificacao.length - 5} planos a mais. Ver lista
                </button>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const planosComissaoSection = planosComissao.length > 0 ? (
          <WorkspaceSection
            color="sky"
            title="Planos da sua comissão"
            subtitle={planosComissao.filter(plano => plano.parecerPendente).length > 0
              ? `Você tem ${planosComissao.filter(plano => plano.parecerPendente).length} parecer${planosComissao.filter(plano => plano.parecerPendente).length > 1 ? 'es' : ''} pendente${planosComissao.filter(plano => plano.parecerPendente).length > 1 ? 's' : ''}.`
              : 'Acompanhe os planos vinculados à sua comissão e o andamento de cada análise.'}
            collapsed={isBoxCollapsed('planos-comissao')}
            onToggleCollapse={() => toggleBox('planos-comissao')}
          >
            <div className="space-y-2 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
              {planosComissao.slice(0, 5).map(plano => (
                <div key={plano.planoTrabalhoId} onClick={() => navigate(`/dashboard/plano/full/${plano.planoTrabalhoId}`)}
                  className="cursor-pointer rounded-2xl border border-sky-200 bg-white/90 px-4 py-3 transition-colors hover:bg-white dark:border-sky-500/40 dark:bg-slate-950/70 dark:hover:bg-slate-900">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${plano.parecerPendente ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>
                      {plano.statusPainel}
                    </span>
                    {plano.numeroDaComissao != null && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                        Comissão {plano.numeroDaComissao}
                      </span>
                    )}
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {plano.pareceresConcluidos}/{plano.totalMembros} pareceres
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${plano.ataAnexada ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {plano.ataAnexada ? 'Ata anexada' : 'Ata pendente'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{plano.titulo}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {[plano.emendaCodigo ? `Emenda ${plano.emendaCodigo}` : null, plano.etapaAtual || plano.status || null].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-sky-700">Abrir</span>
                  </div>
                </div>
              ))}
              {planosComissao.length > 5 && (
                <button type="button" onClick={() => navigate('/dashboard/gestao/planos-trabalho')}
                  className="text-xs font-medium text-sky-700 underline pl-1 hover:text-sky-900">
                  {planosComissao.length - 5} planos a mais. Ver lista completa
                </button>
              )}
            </div>
          </WorkspaceSection>
        ) : null;

        const procuracoesSection = procuracoesPendentes.length > 0 ? (
          <WorkspaceSection
            color="indigo"
            title={procuracoesPendentes.length === 1 ? 'Você tem uma procuração aguardando sua resposta.' : `Você tem ${procuracoesPendentes.length} procurações aguardando sua resposta.`}
            subtitle={procuracoesPendentes.length === 1
              ? `${procuracoesPendentes[0].instituicaoNome || 'Uma instituição'} concedeu procuração a você. Aceite para ter acesso, ou recuse.`
              : 'Aceite para ter acesso às instituições, ou recuse.'}
            collapsed={isBoxCollapsed('procuracao-pendente')}
            onToggleCollapse={() => toggleBox('procuracao-pendente')}
          >
            <div className="border-t border-slate-100 px-5 py-3 dark:border-slate-800">
              <button onClick={() => navigate('/dashboard/procuracoes')}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600">
                Ver procurações
              </button>
            </div>
          </WorkspaceSection>
        ) : null;

        const isOrcamentoRole = role === 'ORCAMENTO';
        const showsOrcamentoGroup = isOrcamentoRole && Boolean(aguardandoDevolucaoSection || aguardandoAcaoSection);
        const showsSecretariaGroup = isComissaoWorkspace && Boolean(aguardandoAcaoSection || planosComissaoSection);
        const showsGestorGroup = isGestor && Boolean(gestorDecisaoSection || pendentesRetificacao.length > 0);
        const showsOperadorGroup = isOperador && Boolean(
          diligenciasResolverSection || ajustesSolicitadosSection || notasFiscaisSection
          || bensDevolucaoSection || pendentesRetificacao.length > 0 || procuracoesSection
        );
        const showsAdminGroup = isAdmin && pendentesRetificacao.length > 0;
        // Comissão fora do contexto Secretaria/Convênios (ex.: Operador ou Orçamento
        // também vinculado a uma comissão) continua visível, como grupo isolado.
        const showsComissaoFallback = !isConvenios && !isComissaoWorkspace && Boolean(planosComissaoSection);

        return (
          <>
            {showsOrcamentoGroup && (
              <WorkspaceSectionGroup>
                {aguardandoDevolucaoSection}
                {aguardandoAcaoSection}
              </WorkspaceSectionGroup>
            )}

            {showsSecretariaGroup && (
              <WorkspaceSectionGroup>
                {aguardandoAcaoSection}
                {planosComissaoSection}
              </WorkspaceSectionGroup>
            )}

            {showsGestorGroup && (
              <WorkspaceSectionGroup>
                {gestorDecisaoSection}
                {renderRetificacaoSection(false)}
              </WorkspaceSectionGroup>
            )}

            {showsOperadorGroup && (
              <WorkspaceSectionGroup>
                {diligenciasResolverSection}
                {ajustesSolicitadosSection}
                {notasFiscaisSection}
                {bensDevolucaoSection}
                {renderRetificacaoSection(true)}
                {procuracoesSection}
              </WorkspaceSectionGroup>
            )}

            {showsAdminGroup && (
              <WorkspaceSectionGroup>
                {renderRetificacaoSection(false)}
              </WorkspaceSectionGroup>
            )}

            {showsComissaoFallback && (
              <WorkspaceSectionGroup>
                {planosComissaoSection}
              </WorkspaceSectionGroup>
            )}
          </>
        );
      })()}

      {!loading && isOperador && !hasInstituicoes && procuracoesPendentes.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-4 dark:border-amber-500/40 dark:bg-amber-950/20">
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">Você ainda não está vinculado a nenhuma instituição.</p>
                <p className="text-sm text-amber-700 mt-1 dark:text-amber-400">Cadastre ou vincule-se a uma instituição para ter acesso completo ao sistema.</p>
              </div>
              {renderCollapseButton('sem-instituicao', 'border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30')}
            </div>
            {!isBoxCollapsed('sem-instituicao') && (
            <button onClick={() => navigate('/dashboard/cadastro-dados-institucionais')}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600">
              Vincular Instituição
            </button>
            )}
          </div>
        </div>
      )}

      {!loading && hasInstituicoes && instituicoes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Minha instituição</span>
          {instituicoes.map(inst => (
            <button key={inst.id} type="button"
              onClick={() => navigate(`/dashboard/cadastro-dados-institucionais?id=${inst.id}`)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-800 transition-colors hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700">
              {/* "Ativo/Inativo" era o vínculo do usuário; o que importa aqui é o status da OSC (ex.: Reprovado). */}
              {inst.ativo && inst.statusOSC ? (
                <StatusOSCBadge status={inst.statusOSC as StatusOSC} size="sm" />
              ) : (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${inst.ativo ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>{inst.ativo ? 'Ativo' : 'Vínculo inativo'}</span>
              )}
              {inst.nomeFantasia || inst.razaoSocial}
            </button>
          ))}
          <span className="flex-1" />
          {!isOperadorSecretaria && (
            <button type="button" onClick={() => navigate('/dashboard/cadastro-dados-institucionais')}
              className="text-xs font-medium text-indigo-600 border border-indigo-300 px-3 py-1 rounded-lg hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950/30 transition-colors">
              Nova instituição
            </button>
          )}
        </div>
      )}

      {!loading && !isWorkflow && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      {/* Emendas recentes — oculto para workflow roles */}
      {emendas.length > 0 && (
        <div className={`${fixedCardClass} border-slate-200 dark:border-slate-700`}>
          <div className="flex items-center justify-between bg-slate-50 px-5 py-3 dark:bg-slate-900 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">Emendas Recentes</span>
              <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                ({emendas.length} · {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValor)})
              </span>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => navigate('/dashboard/emendas')}
              className="text-xs font-medium text-slate-600 border border-slate-200 px-3 py-1 rounded-lg hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors">
              Ver todas
            </button>
            {renderCollapseButton('emendas-recentes')}
            </div>
          </div>
          {!isBoxCollapsed('emendas-recentes') && (
          <div className={`${fixedCardBodyClass} bg-white dark:bg-slate-950`}>
            {/* Chips de filtro por status */}
            <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              {([
                { key: 'todas',      label: 'Todas',      count: emendasList.length,    active: 'bg-indigo-600 text-white',  idle: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
                { key: 'devolvidas', label: 'Devolvidas', count: emendasCountDevolvidas, active: 'bg-red-600 text-white',     idle: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300' },
                { key: 'iniciadas',  label: 'Iniciadas',  count: emendasCountIniciadas,  active: 'bg-blue-600 text-white',    idle: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
                { key: 'concluidas', label: 'Concluídas', count: emendasCountConcluidas, active: 'bg-emerald-600 text-white', idle: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
              ] as const).map(c => (
                <button key={c.key} type="button" onClick={() => setEmendaFilter(c.key)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${emendaFilter === c.key ? c.active : c.idle}`}>
                  {c.label} {c.count}
                </button>
              ))}
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {emendasFiltradas.slice(0, 5).map(emenda => {
                const dev = emendaCategoria(emenda) === 'devolvidas';
                return (
                  <div key={emenda.id}
                    className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 ${dev ? 'border-l-2 border-red-400' : ''}`}
                    onClick={() => navigate('/dashboard/emendas')}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${etapaBadgeClass(emendaEtapa(emenda))}`}>
                          {emendaEtapa(emenda).label}
                        </span>
                        {emenda.codigoOficial && (
                          <span className="font-mono text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded dark:bg-slate-800 dark:text-slate-400">
                            {emenda.codigoOficial}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{emenda.instituicaoNome || '—'}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(emenda.valor)}
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500">{emenda.parlamentarNome || '—'}</div>
                    </div>
                  </div>
                );
              })}
              {emendasFiltradas.length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-slate-400 dark:text-slate-500">Nenhuma emenda nesse filtro.</div>
              )}
              {emendasFiltradas.length > 5 && (
                <div className="bg-white px-4 py-2.5 text-center dark:bg-slate-950">
                  <button type="button" onClick={() => navigate('/dashboard/emendas')}
                    className="rounded-lg border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/30">
                    {emendasFiltradas.length - 5} a mais. Ver todas
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Planos de Trabalho — oculto para workflow roles */}
      {(isAdmin || isGestor || hasInstituicoes) && (
        <PlanosTrabalhoBox
          planos={planos}
          onOpenPlano={(id) => navigate(`/dashboard/plano/full/${id}`)}
          onVerTodos={() => navigate(planosListPath)}
          onNovoPlano={isLegislative ? undefined : () => navigate('/dashboard/novo-plano')}
          emptyActionLabel={isLegislative ? 'Ver planos de trabalho' : 'Criar Plano de Trabalho'}
          collapsed={isBoxCollapsed('planos-trabalho')}
          onToggleCollapse={() => toggleBox('planos-trabalho')}
        />
      )}
        </div>
      )}

      {/* Área de trabalho Convênios — um bloco único, ordenado por prioridade */}
      {!loading && isConvenios && (() => {
        const ETAPAS_ACAO = ['CONVENIOS_ANALISE', 'CONVENIOS_MINUTA', 'CONVENIOS_FORMALIZACAO'];
        const pendentesAcao = planos.filter(p => p.etapaAtual && ETAPAS_ACAO.includes(p.etapaAtual));
        const pendentesRetificacao = planos.filter(p => p.status === 'PENDENTE_RETIFICACAO');
        const etapaLabel: Record<string, string> = {
          CONVENIOS_ANALISE: 'Análise',
          CONVENIOS_MINUTA: 'Minuta',
          CONVENIOS_FORMALIZACAO: 'Formalização',
        };
        const etapaBadge: Record<string, string> = {
          CONVENIOS_ANALISE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
          CONVENIOS_MINUTA: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
          CONVENIOS_FORMALIZACAO: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
        };
        const btnVer = 'shrink-0 px-3 py-1 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors';
        const hasAnyCard = planosComissao.length > 0 || pendentesAcao.length > 0 || docsVencidos.length > 0
          || diligenciasPendentesOperador.length > 0 || diligenciasParaConfirmar.length > 0
          || conveniosQueue.length > 0 || dirigentesQueue.length > 0 || pendentesRetificacao.length > 0;
        if (!hasAnyCard) return null;
        return (
          <WorkspaceSectionGroup>

            {/* 1. Planos pendentes de retificação */}
            {pendentesRetificacao.length > 0 && (
              <WorkspaceSection
                color="orange"
                title={pendentesRetificacao.length === 1 ? '1 plano pendente de retificação' : `${pendentesRetificacao.length} planos pendentes de retificação`}
                subtitle="A Procuradoria Geral Municipal solicitou ajustes. Aguardando o operador corrigir e reenviar para análise."
                collapsed={isBoxCollapsed('retificacoes')}
                onToggleCollapse={() => toggleBox('retificacoes')}
              >
                <div className="space-y-1.5 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
                  {pendentesRetificacao.slice(0, 5).map(p => (
                    <div key={p.id} onClick={() => navigate(`/dashboard/plano/full/${p.id}`)}
                      className="flex items-center justify-between rounded-xl border border-orange-200 bg-white/80 px-3 py-2 cursor-pointer hover:bg-white transition-colors dark:border-orange-500/40 dark:bg-slate-950/70 dark:hover:bg-slate-900">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-slate-800 truncate block dark:text-slate-200">{p.titulo}</span>
                        {p.emendaCodigo && <span className="text-xs text-slate-400">Emenda: {p.emendaCodigo}</span>}
                      </div>
                      <span className="ml-3 shrink-0 text-xs text-orange-700 font-medium dark:text-orange-400">Ver</span>
                    </div>
                  ))}
                  {pendentesRetificacao.length > 5 && (
                    <button type="button" onClick={() => navigate('/dashboard/gestao/planos-trabalho')}
                      className="text-xs font-medium text-orange-700 underline pl-1 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-200">
                      {pendentesRetificacao.length - 5} planos a mais. Ver lista
                    </button>
                  )}
                </div>
              </WorkspaceSection>
            )}

            {/* 2. Planos aguardando Convênios */}
            {pendentesAcao.length > 0 && (
              <WorkspaceSection
                color="orange"
                title="Planos aguardando Convênios"
                count={pendentesAcao.length}
                actionLabel="Ver todos"
                onAction={() => navigate(planosListPath)}
                collapsed={isBoxCollapsed('convenios-acoes')}
                onToggleCollapse={() => toggleBox('convenios-acoes')}
              >
                <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                  {pendentesAcao.slice(0, 5).map((p: PlanoTrabalho) => (
                    <div key={p.id} onClick={() => navigate(`/dashboard/plano/full/${p.id}`)}
                      className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-orange-50 cursor-pointer dark:hover:bg-orange-950/20">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {p.etapaAtual && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${etapaBadge[p.etapaAtual] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                              {etapaLabel[p.etapaAtual] ?? p.etapaAtual}
                            </span>
                          )}
                          <span className="text-sm font-medium text-slate-800 truncate dark:text-slate-100">{p.titulo}</span>
                        </div>
                        {p.instituicaoNome && <div className="text-xs text-slate-400 mt-0.5 truncate dark:text-slate-500">{p.instituicaoNome}</div>}
                      </div>
                      <button className={btnVer}>Ver</button>
                    </div>
                  ))}
                  {pendentesAcao.length > 5 && (
                    <div className="px-4 py-2.5 text-center bg-white dark:bg-slate-950">
                      <button onClick={() => navigate(planosListPath)} className="text-xs font-medium text-orange-700 border border-orange-200 rounded-lg px-3 py-1 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-700 dark:hover:bg-orange-950/30 transition-colors">
                        {pendentesAcao.length - 5} a mais. Ver todos
                      </button>
                    </div>
                  )}
                </div>
              </WorkspaceSection>
            )}

            {/* 3. Diligências — abas: o que está com Convênios (para confirmar) e o que está com o operador */}
            {(diligenciasParaConfirmar.length > 0 || diligenciasPendentesOperador.length > 0) && (() => {
              const qtdConfirmar = diligenciasParaConfirmar.length;
              const qtdOperador = diligenciasPendentesOperador.length;
              const abaAtiva: 'confirmar' | 'operador' =
                diligenciasTab === 'operador' && qtdOperador > 0 ? 'operador'
                  : diligenciasTab === 'confirmar' && qtdConfirmar > 0 ? 'confirmar'
                    : qtdConfirmar > 0 ? 'confirmar' : 'operador';
              const abas: { key: 'confirmar' | 'operador'; label: string; count: number }[] = [
                { key: 'confirmar', label: 'Para confirmar', count: qtdConfirmar },
                { key: 'operador', label: 'Com o operador', count: qtdOperador },
              ];
              return (
              <WorkspaceSection
                color={abaAtiva === 'confirmar' ? 'blue' : 'amber'}
                title="Diligências"
                collapsed={isBoxCollapsed('dilig-para-confirmar')}
                onToggleCollapse={() => toggleBox('dilig-para-confirmar')}
              >
                <WorkspaceTabPills tabs={abas} active={abaAtiva} onSelect={setDiligenciasTab} />
                {abaAtiva === 'confirmar' ? (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                      {diligenciasParaConfirmar.slice(0, 5).map(d => (
                        <div key={d.id} className="px-4 py-3">
                          <p className="text-sm text-slate-800 line-clamp-2 dark:text-slate-100">{d.mensagem}</p>
                          {d.resolucaoObservacao && (
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400"><span className="font-medium">Operador respondeu:</span> {d.resolucaoObservacao}</p>
                          )}
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-400">{d.resolvidoPor} · {d.resolvidoEm ? new Date(d.resolvidoEm).toLocaleDateString('pt-BR') : ''}</span>
                            <button type="button" onClick={() => navigate(d.planoTrabalhoId ? `/dashboard/plano/full/${d.planoTrabalhoId}` : `/dashboard/cadastro-dados-institucionais?id=${d.instituicaoId}`)}
                              className="text-xs font-medium text-blue-700 underline hover:text-blue-900 dark:text-blue-400">
                              {d.planoTrabalhoId ? 'Ver plano de trabalho →' : 'Ver instituição →'}
                            </button>
                          </div>
                          <div className="mt-2 flex justify-end gap-2">
                            <button type="button" disabled={verificandoId === d.id}
                              onClick={async () => {
                                const motivo = window.prompt('Motivo da rejeição (a diligência volta para o operador):');
                                if (motivo === null) return;
                                setVerificandoId(d.id);
                                try {
                                  await diligenciaDocumentoService.verificar(d.id, false, motivo);
                                  setDiligenciasParaConfirmar(prev => prev.filter(item => item.id !== d.id));
                                  setDiligenciasPendentesOperador(prev => [...prev, { ...d, status: 'PENDENTE', rejeicaoMotivo: motivo }]);
                                } catch { notifyError('Erro ao rejeitar diligência. Tente novamente.'); }
                                finally { setVerificandoId(null); }
                              }}
                              className="rounded-lg border border-red-300 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                              Rejeitar
                            </button>
                            <button type="button" disabled={verificandoId === d.id}
                              onClick={async () => {
                                setVerificandoId(d.id);
                                try {
                                  await diligenciaDocumentoService.verificar(d.id, true);
                                  setDiligenciasParaConfirmar(prev => prev.filter(item => item.id !== d.id));
                                } catch { notifyError('Erro ao confirmar diligência. Tente novamente.'); }
                                finally { setVerificandoId(null); }
                              }}
                              className="rounded-lg bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                              {verificandoId === d.id ? 'Confirmando...' : 'Confirmar resolução'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                      {diligenciasPendentesOperador.slice(0, 5).map(d => (
                        <div key={d.id} className="px-4 py-3">
                          <p className="text-sm text-slate-800 line-clamp-2 dark:text-slate-100">{d.mensagem}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-400">{d.enviadoPor} · {d.enviadoEm ? new Date(d.enviadoEm).toLocaleDateString('pt-BR') : ''}</span>
                            <button type="button" onClick={() => navigate(d.planoTrabalhoId ? `/dashboard/plano/full/${d.planoTrabalhoId}` : `/dashboard/cadastro-dados-institucionais?id=${d.instituicaoId}`)}
                              className="text-xs font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-400">
                              {d.planoTrabalhoId ? 'Ver plano de trabalho →' : 'Ver instituição →'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                )}
              </WorkspaceSection>
              );
            })()}

            {/* 4. Cadastros e documentos — abas: Fila de Instituições, Fila de Diretores, Documentos vencidos */}
            {(conveniosQueue.length > 0 || dirigentesQueue.length > 0 || docsVencidos.length > 0) && (() => {
              const abas: { key: 'instituicoes' | 'diretores' | 'documentos'; label: string; count: number }[] = [
                { key: 'instituicoes', label: 'Fila de Instituições', count: conveniosQueue.length },
                { key: 'diretores', label: 'Fila de Diretores', count: dirigentesQueue.length },
                { key: 'documentos', label: 'Documentos vencidos', count: docsVencidos.length },
              ];
              const abaAtiva = (abas.find(a => a.key === cadastrosTab && a.count > 0) ?? abas.find(a => a.count > 0))!.key;
              const atualizarFilaInstituicoes = async () => {
                let q = await institutionService.getQueue().catch(() => []);
                if (!Array.isArray(q) || q.length === 0) {
                  const allForUser = await institutionService.listForUser().catch(() => []);
                  q = (allForUser || []).filter((it: any) => isInstituicaoNaFila(it));
                }
                setConveniosQueue(q);
              };
              const atualizarFilaDiretores = async () => { const dq = await dirigenteService.getQueue().catch(() => []); setDirigentesQueue(dq); };
              const cor = abaAtiva === 'instituicoes' ? 'slate' : abaAtiva === 'diretores' ? 'violet' : 'red';
              const acaoLabel = abaAtiva === 'documentos' ? 'Ver todas' : 'Atualizar';
              const acao = abaAtiva === 'instituicoes' ? atualizarFilaInstituicoes
                : abaAtiva === 'diretores' ? atualizarFilaDiretores
                  : () => navigate('/dashboard/instituicoes');
              return (
              <WorkspaceSection
                color={cor}
                title="Cadastros e documentos"
                actionLabel={acaoLabel}
                onAction={acao}
                collapsed={isBoxCollapsed('cadastros-documentos')}
                onToggleCollapse={() => toggleBox('cadastros-documentos')}
              >
                <WorkspaceTabPills tabs={abas} active={abaAtiva} onSelect={setCadastrosTab} />
                {abaAtiva === 'instituicoes' ? (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                      {conveniosQueue.length === 0 ? (
                        <div className="px-5 py-6 text-center text-sm text-slate-500">
                          <div>Nenhuma instituição na fila no momento.</div>
                          <div className="mt-2">
                            <button onClick={async () => {
                              setConveniosQueueDiag('Executando diagnóstico...');
                              try {
                                const token = sessionStorage.getItem('token');
                                const userStr = sessionStorage.getItem('user');
                                let userObj = null;
                                try { userObj = userStr ? JSON.parse(userStr) : null; } catch (e) { userObj = userStr; }

                                const results: string[] = [];

                                // institutions/queue
                                try {
                                  const resp = await api.get('/institutions/queue');
                                  results.push(`queue: OK ${resp.status} - keys: ${Object.keys(resp.data || {}).join(', ')} - length: ${Array.isArray(resp.data) ? resp.data.length : 'n/a'}`);
                                  results.push(`queue_sample: ${JSON.stringify(Array.isArray(resp.data) ? resp.data.slice(0,5) : resp.data).slice(0,2000)}`);
                                } catch (err: any) {
                                  const status = err?.response?.status ?? 'NO_RESPONSE';
                                  const data = err?.response?.data ? JSON.stringify(err.response.data) : err.message || String(err);
                                  results.push(`queue: ERROR ${status} - ${data}`);
                                }

                                // institutions/for-user
                                try {
                                  const resp2 = await api.get('/institutions/for-user');
                                  results.push(`for-user: OK ${resp2.status} - keys: ${Object.keys(resp2.data || {}).join(', ')} - length: ${Array.isArray(resp2.data) ? resp2.data.length : 'n/a'}`);
                                  results.push(`for-user_sample: ${JSON.stringify(Array.isArray(resp2.data) ? resp2.data.slice(0,5) : resp2.data).slice(0,2000)}`);
                                } catch (err: any) {
                                  const status = err?.response?.status ?? 'NO_RESPONSE';
                                  const data = err?.response?.data ? JSON.stringify(err.response.data) : err.message || String(err);
                                  results.push(`for-user: ERROR ${status} - ${data}`);
                                }

                                // dirigentes/queue
                                try {
                                  const resp3 = await api.get('/dirigentes/queue');
                                  results.push(`dirigentes.queue: OK ${resp3.status} - keys: ${Object.keys(resp3.data || {}).join(', ')} - length: ${Array.isArray(resp3.data) ? resp3.data.length : 'n/a'}`);
                                  results.push(`dirigentes_sample: ${JSON.stringify(Array.isArray(resp3.data) ? resp3.data.slice(0,5) : resp3.data).slice(0,2000)}`);
                                } catch (err: any) {
                                  const status = err?.response?.status ?? 'NO_RESPONSE';
                                  const data = err?.response?.data ? JSON.stringify(err.response.data) : err.message || String(err);
                                  results.push(`dirigentes.queue: ERROR ${status} - ${data}`);
                                }

                                results.push(`token_present: ${!!token}`);
                                results.push(`user_localstorage: ${userStr ? (typeof userObj === 'object' ? JSON.stringify({ id: userObj.id, role: userObj.role, name: userObj.name }).slice(0,500) : String(userObj)) : 'null'}`);

                                setConveniosQueueDiag(results.join('\n\n'));
                              } catch (errAny) {
                                setConveniosQueueDiag(`DIAGNOSTIC-FAIL: ${String(errAny)}`);
                              }
                            }} className="mt-2 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">Diagnosticar fila</button>
                          </div>
                          {conveniosQueueDiag && (
                            <pre className="mt-3 text-left text-xs text-slate-500 bg-gray-50 p-3 rounded max-h-48 overflow-auto">{conveniosQueueDiag}</pre>
                          )}
                        </div>
                      ) : (
                        conveniosQueue.slice(0, 5).map((inst: any) => (
                          <div key={inst.id || inst.institutionId} className="flex cursor-pointer items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-900">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{inst.nomeFantasia || inst.razaoSocial}</div>
                              {(() => {
                                const selo = FILA_INSTITUICAO_SELO[inst.statusAprovacao || ''];
                                return selo
                                  ? <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${selo.classe}`}>{selo.label}</span>
                                  : <div className="text-xs text-slate-400">Status: {inst.statusAprovacao || '—'}</div>;
                              })()}
                            </div>
                            <div className="flex items-center gap-2">
                              {ENABLE_MOVE_TO_END && (
                                <button disabled={movingIds.includes(inst.id || inst.institutionId)} onClick={async (e) => {
                                  e.stopPropagation();
                                  const id = inst.id || inst.institutionId;
                                  try {
                                    setMovingIds(prev => [...prev, id]);
                                    await institutionService.moveToEndQueue(id);

                                    // Prefer listForUser first (faster), then fallback to getQueue
                                    let q = await institutionService.listForUser({ timeout: 7000 }).catch((err) => { console.warn('[DashboardHomePage] listForUser failed after move:', err); return []; });
                                    q = (q || []).filter((it: any) => isInstituicaoNaFila(it));
                                    if (!Array.isArray(q) || q.length === 0) {
                                      try {
                                        const q2 = await institutionService.getQueue({ timeout: 7000 });
                                        q = Array.isArray(q2) ? q2 : [];
                                      } catch (err) {
                                        console.warn('[DashboardHomePage] getQueue fallback failed after move:', err);
                                        q = [];
                                      }
                                    }
                                    setConveniosQueue(q || []);
                                  } catch (err: any) {
                                    const status = err?.response?.status ?? err?.status ?? 'NO_RESPONSE';
                                    const data = err?.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err?.message || String(err);
                                    const msg = `Erro ao mover instituição ${inst.nomeFantasia || id}: status=${status} - ${data}`;
                                    try { notifyError(msg); } catch (_) { }
                                    setConveniosQueueDiag(prev => [prev, msg].filter(Boolean).join('\n\n'));
                                  } finally {
                                    setMovingIds(prev => prev.filter(x => x !== id));
                                  }
                                }}
                                  className="px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                  {movingIds.includes(inst.id || inst.institutionId) ? 'Movendo...' : 'Mover para fim'}
                                </button>
                              )}
                              <button onClick={() => navigate(`/dashboard/cadastro-dados-institucionais?id=${inst.id || inst.institutionId}`)}
                                className="px-3 py-1 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors">Ver</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                ) : abaAtiva === 'diretores' ? (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                      {dirigentesQueue.slice(0, 5).map((d: any) => (
                        <div key={d.id} className="flex cursor-pointer items-center justify-between px-4 py-3 gap-3 hover:bg-slate-50 dark:hover:bg-slate-900">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800 truncate dark:text-slate-100">{d.nomeCompleto || d.nome}</div>
                            <div className="text-xs text-slate-400 dark:text-slate-500">Cargo: {d.cargo || '—'}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {ENABLE_MOVE_TO_END && (
                              <button disabled={movingIds.includes(d.id)} onClick={async (e) => {
                                e.stopPropagation();
                                const id = d.id;
                                try {
                                  setMovingIds(prev => [...prev, id]);
                                  await dirigenteService.moveToEndQueue(id);
                                  let dq = await dirigenteService.getQueue().catch(() => []);
                                  setDirigentesQueue(dq || []);
                                } catch (err: any) {
                                  const status = err?.response?.status ?? err?.status ?? 'NO_RESPONSE';
                                  const data = err?.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err?.message || String(err);
                                  const msg = `Erro ao mover dirigente ${d.nome || id}: status=${status} - ${data}`;
                                  try { notifyError(msg); } catch (_) { }
                                  setConveniosQueueDiag(prev => [prev, msg].filter(Boolean).join('\n\n'));
                                } finally {
                                  setMovingIds(prev => prev.filter(x => x !== (d.id)));
                                }
                              }}
                                className="px-3 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950/30">
                                {movingIds.includes(d.id) ? 'Movendo...' : 'Mover para fim'}
                              </button>
                            )}
                            <button onClick={() => navigate(`/dashboard/cadastro-dirigente?id=${d.id}`)}
                              className="px-3 py-1 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors">Ver</button>
                          </div>
                        </div>
                      ))}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
                      {docsVencidos.slice(0, 5).map((a: AlertaInstituicao) => (
                        <div key={a.instituicaoId} onClick={() => navigate(`/dashboard/cadastro-dados-institucionais?id=${a.instituicaoId}`)}
                          className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-red-50 cursor-pointer dark:hover:bg-red-950/20">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-slate-800 truncate block dark:text-slate-100">{a.razaoSocial}</span>
                            <span className="text-xs text-red-600 mt-0.5 block dark:text-red-400">{a.totalVencidos} documento{a.totalVencidos > 1 ? 's' : ''} vencido{a.totalVencidos > 1 ? 's' : ''}</span>
                          </div>
                          <button className={btnVer}>Ver</button>
                        </div>
                      ))}
                      {docsVencidos.length > 5 && (
                        <div className="px-4 py-2.5 text-center bg-white dark:bg-slate-950">
                          <button onClick={() => navigate('/dashboard/instituicoes')} className="text-xs font-medium text-red-700 border border-red-200 rounded-lg px-3 py-1 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-950/30 transition-colors">
                            {docsVencidos.length - 5} a mais. Ver todas
                          </button>
                        </div>
                      )}
                    </div>
                )}
              </WorkspaceSection>
              );
            })()}

            {/* 5. Planos da sua comissão */}
            {planosComissao.length > 0 && (
              <WorkspaceSection
                color="sky"
                title="Planos da sua comissão"
                subtitle={planosComissao.filter(plano => plano.parecerPendente).length > 0
                  ? `Você tem ${planosComissao.filter(plano => plano.parecerPendente).length} parecer${planosComissao.filter(plano => plano.parecerPendente).length > 1 ? 'es' : ''} pendente${planosComissao.filter(plano => plano.parecerPendente).length > 1 ? 's' : ''}.`
                  : 'Acompanhe os planos vinculados à sua comissão e o andamento de cada análise.'}
                collapsed={isBoxCollapsed('planos-comissao')}
                onToggleCollapse={() => toggleBox('planos-comissao')}
              >
                <div className="space-y-2 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
                  {planosComissao.slice(0, 5).map(plano => (
                    <div key={plano.planoTrabalhoId} onClick={() => navigate(`/dashboard/plano/full/${plano.planoTrabalhoId}`)}
                      className="cursor-pointer rounded-2xl border border-sky-200 bg-white/90 px-4 py-3 transition-colors hover:bg-white dark:border-sky-500/40 dark:bg-slate-950/70 dark:hover:bg-slate-900">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${plano.parecerPendente ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>
                          {plano.statusPainel}
                        </span>
                        {plano.numeroDaComissao != null && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">Comissão {plano.numeroDaComissao}</span>
                        )}
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                          {plano.pareceresConcluidos}/{plano.totalMembros} pareceres
                        </span>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${plano.ataAnexada ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {plano.ataAnexada ? 'Ata anexada' : 'Ata pendente'}
                        </span>
                      </div>
                      <div className="mt-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{plano.titulo}</p>
                          <p className="text-xs text-slate-500 mt-1">{[plano.emendaCodigo ? `Emenda ${plano.emendaCodigo}` : null, plano.etapaAtual || plano.status || null].filter(Boolean).join(' · ')}</p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-sky-700">Abrir</span>
                      </div>
                    </div>
                  ))}
                  {planosComissao.length > 5 && (
                    <button type="button" onClick={() => navigate('/dashboard/gestao/planos-trabalho')}
                      className="text-xs font-medium text-sky-700 underline pl-1 hover:text-sky-900">
                      {planosComissao.length - 5} planos a mais. Ver lista completa
                    </button>
                  )}
                </div>
              </WorkspaceSection>
            )}

          </WorkspaceSectionGroup>
        );
      })()}

        </>
      )}

    </div>
  );
};

export default DashboardHomePage;

